/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { getStore } from '../../reducers';
import { getAccessToken } from '../requestHelper';
import { sleep } from '../utils';

const getWebSocketUrl = async () => {
  const state = getStore().getState();
  const instanceId = state.auth.instanceId;
  const wsHost = state.app.appParam.ink_ai_ws_endpoint;
  const token = await getAccessToken('id');
  return `${wsHost}?instanceId=${instanceId}&token=${token}`;
};

// Open one socket, resolving on 'open' and REJECTING on error/close/timeout.
// The previous implementation polled readyState in a while-loop that only ever
// exited on OPEN (or logout), so a socket that failed to connect — e.g. right
// after a laptop wakes from sleep and the network isn't ready yet — left the
// promise pending forever. setConnection was then never called, connection
// stayed `undefined`, and the heartbeat logged "Waiting for initiation of ws"
// with no way to recover. Making failure REJECT lets setupConnection's catch
// run setConnection(null), which the heartbeat treats as "reset and reconnect".
const openSocket = (url: string, timeoutMs = 10000) =>
  new Promise<WebSocket>((resolve, reject) => {
    const ws = new WebSocket(url);
    const timer = setTimeout(() => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
      reject(new Error('WebSocket open timed out'));
    }, timeoutMs);
    ws.addEventListener(
      'open',
      () => {
        clearTimeout(timer);
        resolve(ws);
      },
      { once: true },
    );
    ws.addEventListener(
      'error',
      () => {
        clearTimeout(timer);
        reject(new Error('WebSocket connection error'));
      },
      { once: true },
    );
    ws.addEventListener(
      'close',
      (e) => {
        clearTimeout(timer);
        reject(new Error(`WebSocket closed before open: ${e.code} ${e.reason}`));
      },
      { once: true },
    );
  });

export const getSocketConnection = async (maxAttempts = 3) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Stop retrying once the session is gone (a failed token refresh signs the
    // user out) so we don't loop forever with a dead token.
    if (!getStore().getState().auth.loggedIn) {
      throw new Error('Not logged in; aborting WebSocket connection');
    }
    try {
      return await openSocket(await getWebSocketUrl());
    } catch (err) {
      console.warn('[ws] connect attempt failed:', err);
      if (attempt < maxAttempts - 1) {
        await sleep(3000);
      }
    }
  }
  // All attempts failed: reject so the caller (setupConnection) sets
  // connection=null and the heartbeat retriggers a fresh reconnect cycle.
  throw new Error('WebSocket connection failed after retries');
};
